import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  Output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IconChevronComponent } from '../icon-chevron/icon-chevron.component';
import { DropdownCoordinatorService } from '../../../core/services/dropdown-coordinator/dropdown-coordinator.service';

export type SelectVariant = 'pill' | 'form';
export type SelectSummaryFormat = 'count' | 'label';
export type SelectAlign = 'left' | 'right';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, FormsModule, IconChevronComponent],
  templateUrl: './select.component.html',
  styleUrl: './select.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ]
})
export class SelectComponent implements ControlValueAccessor {
  private static nextId = 0;
  private readonly autoId = `app_select_${SelectComponent.nextId++}`;

  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);
  private dropdownCoordinator = inject(DropdownCoordinatorService);

  @Input() id: string = '';
  @Input() name: string = '';
  @Input() variant: SelectVariant = 'pill';
  @Input() items: any[] = [];
  @Input() bindLabel: string = 'label';
  @Input() bindValue: string = 'value';
  @Input() multiple: boolean = false;
  @Input() searchable: boolean = true;
  @Input() translateLabels: boolean = false;
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() searchPlaceholder: string = '';
  @Input() emptyMessage: string = '';
  @Input() pillClass: string = 'w-[165px]';
  @Input() dropdownClass: string = 'min-w-[240px] max-w-xs';
  @Input() align: SelectAlign = 'right';
  @Input() summaryFormat: SelectSummaryFormat = 'count';
  @Input('summary') customSummary?: string | null = undefined;
  @Input() disabled: boolean = false;
  @Input() clearable: boolean = false;
  @Input() allowDeselect: boolean = true;
  @Input() closeOnSelect: boolean = true;
  @Input() error?: string | null = null;
  @Input() reserveErrorSpace: boolean = false;
  @Input() customClass: string = '';

  @Output() selectionChange = new EventEmitter<any>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();

  get selectId(): string {
    return this.id || this.autoId;
  }

  isOpen = computed(() => this.dropdownCoordinator.isOpen(this.selectId));
  searchTerm = signal<string>('');

  value: any = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  @HostBinding('class')
  get hostClasses(): string {
    return this.variant === 'form' ? 'block w-full' : 'inline-block';
  }

  get computedDropdownClasses(): string {
    if (this.variant === 'form') {
      return `w-full left-0 min-w-full ${this.dropdownClass}`.trim();
    }
    const alignmentClass = this.align === 'right' ? 'right-0' : 'left-0';
    return `${alignmentClass} min-w-full ${this.dropdownClass}`;
  }

  get resolvedSearchPlaceholder(): string {
    if (this.searchPlaceholder) {
      return this.searchPlaceholder;
    }
    const translated = this.translate.instant('REQUESTLIST.FILTERS.SEARCH_PLACEHOLDER');
    return translated && translated !== 'REQUESTLIST.FILTERS.SEARCH_PLACEHOLDER'
      ? translated
      : 'Rechercher...';
  }

  get resolvedEmptyMessage(): string {
    if (this.emptyMessage) {
      return this.emptyMessage;
    }
    const translated = this.translate.instant('REQUESTLIST.FILTERS.NO_RESULTS');
    return translated && translated !== 'REQUESTLIST.FILTERS.NO_RESULTS'
      ? translated
      : 'Aucun résultat';
  }

  get filteredItems(): any[] {
    const list = this.items || [];
    const term = (this.searchTerm() || '').trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter((item) => {
      const label = this.getItemLabel(item).toLowerCase();
      return label.includes(term);
    });
  }

  get hasSelection(): boolean {
    if (this.multiple) {
      return Array.isArray(this.value) && this.value.length > 0;
    }
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get summary(): string | null {
    if (this.customSummary !== undefined) {
      return this.customSummary;
    }
    if (!this.hasSelection) {
      return null;
    }
    if (this.summaryFormat === 'count') {
      if (this.multiple) {
        return `x${(this.value as any[]).length}`;
      }
      return 'x1';
    }

    // summaryFormat === 'label'
    if (this.multiple) {
      const list = this.value as any[];
      if (list.length === 1) {
        const found = (this.items || []).find((it) =>
          this.areValuesEqual(this.getItemValue(it), list[0])
        );
        return found ? this.getItemLabel(found) : 'x1';
      }
      return `x${list.length}`;
    }

    const found = (this.items || []).find((it) =>
      this.areValuesEqual(this.getItemValue(it), this.value)
    );
    return found ? this.getItemLabel(found) : String(this.value);
  }

  get formDisplayText(): string {
    if (!this.hasSelection) {
      return '';
    }
    if (this.multiple) {
      const list = this.value as any[];
      if (list.length === 0) return '';
      if (list.length === 1) {
        const found = (this.items || []).find((it) =>
          this.areValuesEqual(this.getItemValue(it), list[0])
        );
        return found ? this.getItemLabel(found) : '1 sélectionné';
      }
      return `${list.length} sélectionnés`;
    }
    const found = (this.items || []).find((it) =>
      this.areValuesEqual(this.getItemValue(it), this.value)
    );
    return found ? this.getItemLabel(found) : String(this.value);
  }

  getItemValue(item: any): any {
    if (item === null || item === undefined) return item;
    if (typeof item !== 'object') return item;
    if (this.bindValue && this.bindValue in item) {
      return item[this.bindValue];
    }
    return item;
  }

  getItemLabel(item: any): string {
    if (item === null || item === undefined) return '';
    if (typeof item !== 'object') return String(item);
    let raw = '';
    if (this.bindLabel && this.bindLabel in item) {
      raw = item[this.bindLabel];
    } else if ('label' in item) {
      raw = item.label;
    } else if ('name' in item) {
      raw = item.name;
    } else {
      raw = String(item);
    }
    return this.translateLabels ? this.translate.instant(raw) : raw;
  }

  getItemTrackKey(item: any, index?: number): any {
    if (item === null || item === undefined) return index;
    if (typeof item !== 'object') return item;
    const val = this.getItemValue(item);
    if (val !== undefined && val !== null) return val;
    if ('id' in item) return item.id;
    if ('code' in item) return item.code;
    if ('label' in item) return item.label;
    if ('name' in item) return item.name;
    return index;
  }

  isSelected(item: any): boolean {
    const itemVal = this.getItemValue(item);
    if (this.multiple) {
      if (!Array.isArray(this.value)) return false;
      return this.value.some((v) => this.areValuesEqual(v, itemVal));
    }
    return this.areValuesEqual(this.value, itemVal);
  }

  toggleDropdown(event?: Event): void {
    if (this.disabled) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled) return;
    this.dropdownCoordinator.open(this.selectId);
    this.searchTerm.set('');
    this.opened.emit();
  }

  close(): void {
    if (!this.isOpen()) return;
    this.dropdownCoordinator.close(this.selectId);
    this.searchTerm.set('');
    this.onTouched();
    this.closed.emit();
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.searchChange.emit(term);
  }

  onItemClick(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.toggleItem(item);
  }

  onItemToggle(item: any, event: Event): void {
    event.stopPropagation();
    this.toggleItem(item);
  }

  private toggleItem(item: any): void {
    if (this.disabled) return;
    const itemVal = this.getItemValue(item);

    if (this.multiple) {
      const current = Array.isArray(this.value) ? [...this.value] : [];
      const index = current.findIndex((v) => this.areValuesEqual(v, itemVal));
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(itemVal);
      }
      this.value = current;
      this.onChange(this.value);
      this.selectionChange.emit(this.value);
    } else {
      // Single select
      if (this.allowDeselect && this.areValuesEqual(this.value, itemVal)) {
        this.value = null;
      } else {
        this.value = itemVal;
      }
      this.onChange(this.value);
      this.selectionChange.emit(this.value);
      if (this.closeOnSelect) {
        this.close();
      }
    }
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.value = this.multiple ? [] : null;
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.isOpen() && !this.elementRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  writeValue(val: any): void {
    if (val === null || val === undefined || val === '') {
      this.value = this.multiple ? [] : null;
    } else if (this.multiple) {
      this.value = Array.isArray(val) ? [...val] : [val];
    } else {
      this.value = val;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private areValuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a === 'number' && typeof b === 'string') return String(a) === b;
    if (typeof a === 'string' && typeof b === 'number') return a === String(b);
    return false;
  }
}
